<template lang="pug">
#login
  #pc(v-if="appmode == 'pc'")
    .login-content
      // app logo
      .login-logo.text-center
        img(src="@/assets/login-title.png")
      // userid
      .login-input.mb-3.d-flex.flex-row.justify-content-center.align-items-center
        img.mr-4(src="@/assets/login-id.png")
        input#user-id.form-control(
          v-model="userId"
          maxlength="256"
          type="text"
          name="user-id"
          placeholder="ユーザーID"
          autocomplete="on"
          v-on:keyup.enter="onLogin"
        )
      // password
      .login-input.mb-3.d-flex.flex-row.justify-content-center.align-items-center
        img.mr-4(src="@/assets/login-password.png")
        input#password.form-control(
          v-model="password"
          maxlength="256"
          type="password"
          name="password"
          placeholder="パスワード"
          autocomplete="new-password"
          v-on:keyup.enter="onLogin"
        )
      // error message
      .login-error.text-center.mb-4
        span.error-message(v-show="error") {{ error }}
      // save logininfo checkbox
      .remember-login.mb-4.d-flex.flex-row.justify-content-center.align-items-center
        input#save(
          v-model="save"
          name="save"
          type="checkbox"
          autocomplete="on"
        )
        label(for="save") ログイン情報を保存する
      // login button
      .login-button.text-center
        button#login-btn.btn(type="submit" @click="onLogin") ログイン
      // company logo
      .logo-company.text-center
        img(src="@/assets/login-kmlogo.png")
      .version.mb-3.text-center
        span Version {{ version }}
      .copyright.text-center
        span Copyright 2019 All rights reserved.
    // facility select modal
    FacilitySelectModal(v-model="showFacilitySelectModalByPc" :facilityId.sync="facilityId" :facilityOptions="facilityOptions" type="pc"
      @ok="onLoginByFacility" @cancel="facilityId = null;")
    // loading modal component
    Loading(ref="loading")

  #mobile(v-if="appmode == 'mobile'")
    .login-content
      // app logo
      .login-logo.text-center
        img(src="@/assets/login-title.png")
      // userid
      .login-input.mb-3.d-flex.flex-row.justify-content-center.align-items-center
        img.mr-3(src="@/assets/login-id.png")
        input#user-id.form-control(
          v-model="userId"
          maxlength="256"
          type="text"
          name="user-id"
          placeholder="ユーザーID"
          autocomplete="on"
          v-on:keyup.enter="onLogin"
        )
      // password
      .login-input.mb-3.d-flex.flex-row.justify-content-center.align-items-center
        img.mr-3(src="@/assets/login-password.png")
        input#password.form-control(
          v-model="password"
          maxlength="256"
          type="password"
          name="password"
          placeholder="パスワード"
          autocomplete="new-password"
          v-on:keyup.enter="onLogin"
        )
      // error message
      .login-error.text-center.mb-4
        span.error-message(v-show="error") {{ error }}
      // save logininfo checkbox
      .remember-login.mb-4.d-flex.flex-row.justify-content-center.align-items-center
        input#save(
          v-model="save"
          name="save"
          type="checkbox"
          autocomplete="on"
        )
        label(for="save") ログイン情報を保存する
      // login button
      .login-button.text-center
        button#login-btn.btn(type="submit" @click="onLogin") ログイン
      // company logo
      .logo-company.text-center
        img(src="@/assets/login-kmlogo.png")
      .version.mb-3.text-center
        span Version {{ version }}
      .copyright.text-center
        span Copyright 2019 All rights reserved.
    // facility select modal
    mFacilitySelectModal(v-model="showFacilitySelectModalByMobile" :facilityId.sync="facilityId" :facilityOptions="facilityOptions" type="mobile"
      @ok="onLoginByFacility" @cancel="facilityId = null;")
    // loading modal component
    Loading(ref="loading")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { commonStore, userStore, nationalHolidaysStore, screenInputTemporaryStore } from "@/store/store";

import Setting from "../../../public/setting.json";
import { Url } from "@/commons/constants/Url";
import { PcUrl } from "@/commons/constants/PcUrl";
import { MobileUrl } from "@/commons/constants/MobileUrl";
import AuthorizationService, { LoginByFacilityPostModel, LoginByFacilityResponse, LoginPostModel, LoginResponse } from "@/commons/services/Authorization";
import SystemSettingsService, { SystemSetting } from "@/commons/services/SystemSettings";

import FacilitySelectModal from "@/pc/basics/FacilitySelectModal.vue";
import mFacilitySelectModal from "@/mobile/basics/FacilitySelectModal.vue";

import Loading from "@/pc/basics/Loading.vue";
import { AxiosError } from "axios";
import { FacilityGetModel } from "../services/Facilities";

/**
 * ログイン画面コンポーネント
 */
@Component({
  components: {
    Loading,
    FacilitySelectModal,
    mFacilitySelectModal
  }
})
export default class Login extends Vue {
  /** ログインサービス */
  @Inject("AuthorizationService") authorizationService!: AuthorizationService;
  /** システム設定サービス */
  @Inject("SystemSettingsService") systemSettingsService!: SystemSettingsService;

  /** アプリモード。storeのgetterから値を取得する */
  @Getter("appMode") appmode!: string;

  /** ユーザーID */
  userId: string = "";
  /** パスワード */
  password: string = "";
  /** ログイン情報の保持 */
  save: boolean = false;

  /** ログイン先施設ID */
  facilityId: number | null = null;
  /** 施設選択モーダル選択肢 */
  facilityOptions: Array<{ value: number | string; label: string; text: string }> = [];

  /** 施設選択モーダルで使用する施設リスト */
  facilities: Array<FacilityGetModel> = [];

  /** 施設選択モーダル（PC）表示可否 */
  showFacilitySelectModalByPc: boolean = false;
  /** 施設選択モーダル（PC）表示可否 */
  showFacilitySelectModalByMobile: boolean = false;

  /** バージョン */
  version: string = process.env.VUE_APP_VERSION ? process.env.VUE_APP_VERSION : "";
  /** エラーメッセージ */
  error: string = "";

  /**
   * vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // この画面へ来たタイミングで一度ログイン情報を初期化する
    userStore.logout();
    await this.createView();
    return;
  }

  /**
   * ### 画面情報を作成する
   */
  public async createView(): Promise<void> {
    // ブラウザ(user-agent)のチェック
    const isSupportedAgent: boolean = this.isSupportedAgent();
    if (isSupportedAgent == false) {
      this.$router.replace(Url.UnsupportedAgent);
      return;
    }

    this.userId = "";
    this.password = "";
    this.save = false;

    this.facilityId = null;
    this.facilityOptions = [];

    // cookieにログイン情報がある場合、フォームに設定する
    this.loadCookie();

    // 認証エラーなどでログイン画面へ遷移した場合、storeにエラーメッセージを格納しているので表示する
    // 値をコピーしたものを格納し、storeのメッセージは削除する
    if (commonStore.message) {
      this.error = String(commonStore.message);
      commonStore.setMessage({ message: "" });
    }

    await this.checkAppVersion();

    return;
  }

  /**
   * ### サポートしているブラウザかチェックする
   */
  public isSupportedAgent(): boolean {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const supportAgent: Array<string> = Setting.supportedAgent;

    console.info("userAgent: ", window.navigator.userAgent);

    for (const agent of supportAgent) {
      if (userAgent.indexOf(agent) != -1) {
        return true;
      }
    }

    return false;
  }

  /**
   * ### Cookieを見て、ログイン情報が保持されている場合はフォームに値を格納する
   */
  public loadCookie(): void {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");

      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      if (cookieMap["save"] == "true") {
        this.userId = cookieMap["id"];
        this.password = cookieMap["password"];
        this.save = true;
      }
    }

    return;
  }

  /**
   * ### ローカルストレージのversionとサーバーで保持しているversionを比較する
   */
  public async checkAppVersion(): Promise<void> {
    const version = localStorage.getItem("version");
    const systemSetting: SystemSetting = await this.systemSettingsService.getSystemSetting();

    if (version != systemSetting.version) {
      localStorage.setItem("version", systemSetting.version);
      alert("システムがバージョンアップされました。\nキーボードのF5ボタンもしくはブラウザの更新ボタンでページを更新してお試しください。");
      location.reload();
    }

    return;
  }

  /**
   * ### ログイン処理
   */
  public async onLogin(): Promise<void> {
    // バリデーションチェック
    if (!this.userId || !this.password) {
      this.error = "ユーザーIDとパスワードのいずれかが未入力です";
      return;
    }

    // ローディング表示
    this.showLoading("ログイン中...");

    try {
      // ログインが成功したら施設選択モーダルを表示する
      // 施設を選択する必要が無い場合（選べる施設が1つの場合）は施設へのログインを実行する
      const loginResponse: LoginResponse = await this.login();

      this.facilities = loginResponse.facilities;

      if (loginResponse.facilities.length == 1) {
        this.facilityId = loginResponse.facilities[0].id;
        await this.onLoginByFacility();
      } else if (2 <= loginResponse.facilities.length) {
        this.showFacilitySelectModal(loginResponse);
      }
    } catch (error) {
      this.loginErrorHandle(error as AxiosError);
    } finally {
      this.hideLoading();
    }

    return;
  }

  /**
   * ### ログイン処理
   *
   * - IDとパスワードでログイン認証する
   */
  public async login(): Promise<LoginResponse> {
    const params: LoginPostModel = {
      userId: this.userId,
      password: this.password
    };
    return await this.authorizationService.login(params);
  }

  /**
   * ### 施設にログインする
   */
  public async onLoginByFacility(): Promise<void> {
    // ローディング表示
    this.showLoading("ログイン中...");

    try {
      const loginByFacilityResponse: LoginByFacilityResponse = await this.loginByFacility(this.facilityId as number);
      userStore.login({ loginByFacilityResponse, facilities: this.facilities, password: this.password });

      this.setCookie(this.save);
      await nationalHolidaysStore.init({ userStore: userStore }); // 祝日情報を初期化する

      // 認証エラーなどでログイン画面へ遷移した場合、storeに元いたページを格納しているのでそのページへ移動する
      // ただし、前回ログインした施設IDと同じ施設IDにログインしない場合はログインし直しになるので、元いたページには遷移しない
      // 値をコピーしたものを格納し、storeの遷移先は削除する
      if (commonStore.beforeUrl) {
        const url: string = commonStore.beforeUrl;
        commonStore.setBeforeUrl({ url: "" });

        if (commonStore.beforeLoginFacilityId == this.facilityId) {
          this.$router.push(url);
        } else {
          commonStore.setBeforeLoginFacilityId({ id: this.facilityId as number });
          screenInputTemporaryStore.initializeScreenInputTemporary();

          // アプリモードで遷移先を切り返る
          if (this.appmode == "mobile") {
            this.$router.push(MobileUrl.Dashboard);
          } else {
            this.$router.push(PcUrl.Dashboard);
          }
        }
      } else {
        commonStore.setBeforeLoginFacilityId({ id: this.facilityId as number });
        screenInputTemporaryStore.initializeScreenInputTemporary();

        // アプリモードで遷移先を切り返る
        if (this.appmode == "mobile") {
          this.$router.push(MobileUrl.Dashboard);
        } else {
          this.$router.push(PcUrl.Dashboard);
        }
      }
    } catch (error) {
      this.loginErrorHandle(error as AxiosError);
      this.hideLoading();
    }

    return;
  }

  /**
   * ### 施設ログイン処理
   *
   * IDとパスワード、施設IDでログイン認証する
   */
  public async loginByFacility(facilityId: number): Promise<LoginByFacilityResponse> {
    const params: LoginByFacilityPostModel = {
      userId: this.userId,
      password: this.password,
      facilityId: facilityId
    };
    return await this.authorizationService.loginByFacility(params);
  }

  /**
   * ### 認証情報をCookieに設定する
   *
   * - 設定しない場合はリセットする
   */
  public setCookie(needSave: boolean): void {
    if (needSave == true) {
      // Cookie有効秒数
      // 60(秒) * 60(分) * 24(時) が1日にかかる秒数なので、その秒数に設定情報の日数を乗算する
      const saveSecond = 60 * 60 * 24 * Setting.cookieSaveDay;
      document.cookie = `id=${this.userId}; max-age=${saveSecond};`;
      document.cookie = `password=${this.password}; max-age=${saveSecond};`;
      document.cookie = `save=${this.save}; max-age=${saveSecond};`;
    } else {
      document.cookie = "id=";
      document.cookie = "password=";
      document.cookie = "save=";
    }
    return;
  }

  /** ### 施設選択モーダルを表示する */
  public showFacilitySelectModal(loginResponse: LoginResponse): void {
    const facilityOptions: Array<{ value: number | string; label: string; text: string }> = [];
    for (const facility of loginResponse.facilities) {
      facilityOptions.push({
        value: facility.id,
        label: facility.name,
        text: facility.name
      });
    }
    this.facilityOptions = facilityOptions;

    // PCとモバイルでコンポーネント別々に作る？ひとまずPCのみ作成
    if (this.appmode == "mobile") {
      this.showFacilitySelectModalByMobile = true;
    } else {
      this.showFacilitySelectModalByPc = true;
    }
    return;
  }

  /** ### ログイン時のエラーをハンドリングする */
  public loginErrorHandle(error: AxiosError): void {
    console.debug("loginErrorHandle axiosError:", error);
    if (error.response) {
      // メンテナンス中の場合、メンテナンス画面へ遷移する
      if (error.response.status == 503) {
        this.$router.push(Url.Maintenance);
      }
      // 認証エラーの場合、ステータスコードが401なのでメッセージを分ける
      else if (error.response.status == 401) {
        this.error = "ユーザーIDまたはパスワードが不正です";
      }
    } else {
      this.error = "ログインに失敗しました";
      alert("キーボードのF5ボタンもしくはブラウザの更新ボタンでページを更新してお試しください。");
    }
    return;
  }

  /** ### ローディングモーダル表示処理 */
  public showLoading(message: string): void {
    const loading: Loading | Vue | Element | Vue[] | Element[] = this.$refs.loading;
    if (loading instanceof Loading) {
      loading.show(message);
    }
    return;
  }

  /** ### ローディングモーダル非表示処理 */
  public hideLoading(): void {
    const loading: Loading | Vue | Element | Vue[] | Element[] = this.$refs.loading;
    if (loading instanceof Loading) {
      loading.hide();
    }
    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../styles/pc/_colors.scss";

// コンテンツ全体に対するpadding
$base-padding: 16px;
$base-top-padding: 82px;

// ログイン情報保持のチェックボックスのラベル文字サイズ
$remember-font-size: 16px;
// チェックボックス関連で計算が必要なため、一部値を変数化
// $remember-content-height = チェックボックス画像のサイズになるので、チェックボックスを大きくするように指示があればこの値を変更すること
$remember-content-height: 32px;
$remember-padding: ($remember-content-height - $remember-font-size) / 2;

#login {
  .form-control {
    &:focus {
      border-color: $form-border-select-color;
      box-shadow: 0 0 0 0.2rem $form-boxshadow-select-color;
    }
  }
  // 共通デザイン
  .remember-login {
    // 画像でセレクトボックスを表現するので、input要素自体は非表示にする
    input[type="checkbox"] {
      display: none;

      // input要素の代わりに画像つきのlabelにする
      // 選択しているか否かで画像を切り替える
      // for属性でcheckboxと紐づかせたlabel要素のclickで、チェックボックスの状態が変更される
      & + label {
        // チェックボックス画像が全て表示されるように高さを指定する
        height: $remember-content-height;
        font-weight: normal;
        // チェックボックス画像が文字より大きく、文字が縦中央揃えに出来ないので、paddingで上下余白を取って位置調整
        // フォントのサイズが変更になったらpadding上下の値も変更になるので計算で求める
        padding: $remember-padding 0 $remember-padding $remember-content-height + 8px;
        background: url("../../assets/login-checkbox-off.png") no-repeat left center;
        // チェックボックス画像のサイズを指定
        // 正方形なので要素の高さと同じになる
        background-size: $remember-content-height $remember-content-height;
        cursor: pointer;
        // BOOTSTRAPのスタイル打消し
        margin-bottom: 0 !important;
      }
      &:checked + label {
        background: url("../../assets/login-checkbox-on.png") no-repeat left center;
        // チェックボックス画像のサイズを指定
        // 正方形なので要素の高さと同じになる
        background-size: $remember-content-height $remember-content-height;
      }
    }
  }
  /* エラーメッセージ表示部 */
  .login-error {
    white-space: pre-wrap;
  }
  /* コピーライト */
  .copyright {
    font-size: 0.9rem;
  }

  /* コンテンツ部PC */
  #pc {
    .login-content {
      // 実際のコンテンツ幅はアプリロゴサイズ471px + padding分の幅にする
      width: 471px + ($base-padding * 2);
      // 中央揃えにするための指定
      margin-left: auto;
      margin-right: auto;
      // 余白はデザインガイドラインを受けて変更の可能性有
      // ここで上下左右の余白を作り、この要素の内部の要素は下marginだけ指定して要素間の余白を作る
      padding: $base-top-padding $base-padding 0 $base-padding;

      /* アプリロゴ */
      .login-logo {
        margin-bottom: 1.4rem;
      }
      /* ユーザーID、パスワード入力部 */
      .login-input {
        input {
          width: 90%;
        }
      }
      /* ログインボタン */
      .login-button {
        margin-bottom: 3.1rem;
        button {
          width: 160px;
        }
      }
      /* 会社ロゴ */
      .logo-company {
        // 規定により、ロゴの近くに文言を置いてはいけないためマージン調整
        margin-bottom: 2rem;
      }
    }
  }

  /* コンテンツ部Mobile */
  #mobile {
    .login-content {
      width: 100vw;
      padding: 1rem;

      /* アプリロゴ */
      .login-logo {
        margin-bottom: 1.4rem;
        img {
          // PC用の画像なのでサイズを調整
          width: 242px;
          height: 118px;
        }
      }
      /* ユーザーID、パスワード入力部 */
      .login-input {
        img {
          width: 38px;
          height: 38px;
        }
      }
      /* ログインボタン */
      .login-button {
        margin-bottom: 3.1rem;
        button {
          width: 160px;
        }
      }
      /* 会社ロゴ */
      .logo-company {
        img {
          // PC用の画像なのでサイズを調整
          width: 108px;
          height: 75px;
        }
        // 規定により、ロゴの近くに文言を置いてはいけないためマージン調整
        margin-bottom: 2rem;
      }
    }
  }
}
</style>
